import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { ThemeToggle } from './ThemeToggle';
import { Logo } from './Logo';
import { ChangePasswordModal } from './ChangePasswordModal';
import NotificationBell from './NotificationBell';
import './AppHeader.css';

interface AppHeaderProps {
  showUserInfo?: boolean;
}

export const AppHeader: React.FC<AppHeaderProps> = ({ showUserInfo = false }) => {
  const { user, logout } = useAuth();
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLElement>(null);

  const handleLogout = async () => {
    await logout();
  };

  const toggleProfileDropdown = () => {
    setShowProfileDropdown(!showProfileDropdown);
  };

  const openChangePasswordModal = () => {
    setShowChangePasswordModal(true);
    setShowProfileDropdown(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowProfileDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Dynamically set header height for sidebar positioning
  useEffect(() => {
    const updateHeaderHeight = () => {
      if (headerRef.current) {
        const height = headerRef.current.offsetHeight;
        document.documentElement.style.setProperty('--header-min-height', `${height}px`);
      }
    };

    // Set initial height
    updateHeaderHeight();

    // Update on window resize
    window.addEventListener('resize', updateHeaderHeight);
    return () => window.removeEventListener('resize', updateHeaderHeight);
  }, [showProfileDropdown]); // Update when dropdown state changes

  return (
    <header ref={headerRef} className="app-header">
      <div className="app-header-content">
        <div className="brand">
          <Logo size="medium" variant="header" showText={true} />
        </div>
        
        <div className="header-actions">
          <ThemeToggle />
          {showUserInfo && user && <NotificationBell />}
          {showUserInfo && user && (
            <div className="user-info">
              <span className="user-name">
                Welcome, {user.full_name || user.username}
              </span>
              <span className="user-role">({user.role})</span>
              
              <div className="profile-dropdown-container" ref={dropdownRef}>
                <button 
                  onClick={toggleProfileDropdown} 
                  className="profile-button"
                  aria-label="Profile menu"
                >
                  <div className="profile-avatar">
                    {(user.full_name || user.username).charAt(0).toUpperCase()}
                  </div>
                </button>
                
                {showProfileDropdown && (
                  <div className="profile-dropdown">
                    <button 
                      onClick={openChangePasswordModal} 
                      className="dropdown-item"
                    >
                      Change Password
                    </button>
                    <div className="dropdown-divider"></div>
                    <button 
                      onClick={handleLogout} 
                      className="dropdown-item logout-item"
                    >
                      Logout
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      
      <ChangePasswordModal
        isOpen={showChangePasswordModal}
        onClose={() => setShowChangePasswordModal(false)}
      />
    </header>
  );
};